import i18next from "i18next";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Chapter from "./Chapter";
import VideoJS from "./VideoJS";
import VideoPlayerControls from "./VideoPlayerControls";
const VideoPlayer = ({ initialVideosData, resumeAt, pathName }) => {
  const navigate = useNavigate();
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  function toggleVideoPlay(boolean) {
    setIsVideoPlaying(boolean);
  }
  const [videosProgress, setVideosProgress] = useState();
  const [videosSources, setVideosSources] = useState();
  const [currentVideoId, setCurrentVideoId] = useState();
  const [videoInfo, setVideoInfo] = useState({
    currentTime: "0:00",
    duration: "0:00",
    title: null,
  });
  const [videoJsOptions] = useState({
    autoplay: true,
    controls: false,
    responsive: true,
    fluid: false,
    sources: initialVideosData,
  });
  const playerRef = useRef(null);
  let { id: pathId } = useParams();

  /**
   * Update video info bar
   */
  useEffect(() => {
    if (!videosSources && !currentVideoId) {
      return;
    }
    let videoTitle;
    initialVideosData.forEach((el) => {
      if (el.id === currentVideoId) {
        videoTitle = el.title;
      }
    });
    setVideoInfo((currentVideoInfo) => {
      return {
        ...currentVideoInfo,
        title: videoTitle,
      };
    });
  }, [currentVideoId, videosSources, initialVideosData]);

  /**
   *  Bind captions
   */
  useEffect(() => {
    if (!playerRef.current || !initialVideosData) return;

    // remove old text tracks
    playerRef?.current
      ?.remoteTextTracks()
      .tracks_.forEach((track) =>
        playerRef.current.removeRemoteTextTrack(track)
      );

    if (i18next.resolvedLanguage !== "en") return;
    // add text track to current video
    initialVideosData.forEach((videoSource) => {
      if (videoSource.id === currentVideoId) {
        playerRef?.current?.addRemoteTextTrack(
          {
            kind: "captions",
            src: videoSource.textTracks,
            mode: "showing",
            default: true,
            srcLang: "en",
            label: "en",
            language: "en",
          },
          true
        );
      }
    });
  }, [
    currentVideoId,
    initialVideosData,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    playerRef["current"],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    i18next["resolvedLanguage"],
  ]);

  /**
   * Get videos list, resume at specific point if possible
   */
  useEffect(() => {
    let initialVideosProgress = {};
    let initialVideosSources = {};
    initialVideosData.forEach((videoData) => {
      initialVideosSources = {
        ...initialVideosSources,
        [videoData.id]: videoData.src,
      };
      initialVideosProgress = {
        ...initialVideosProgress,
        [videoData.id]: 0,
      };
    });
    setVideosProgress(initialVideosProgress);
    setVideosSources(initialVideosSources);
    if (!resumeAt) {
      setCurrentVideoId(initialVideosData[0].id);
    }
    if (resumeAt && playerRef) {
      const { videoId, time } = resumeAt;
      if (initialVideosSources[videoId]) {
        setCurrentVideoId(videoId);
        chapterChange.startAt(videoId, time);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVideosData, resumeAt, playerRef]);

  /**
   * Intervaly save progress
   */
  useEffect(() => {
    let interval;
    if (!isNaN(currentVideoId) && playerRef) {
      interval = setInterval(() => {
        if (!playerRef?.current?.paused()) {
          saveProgressData();
        }
      }, 3000);
    }
    function saveProgressData() {
      const time = playerRef.current.currentTime().toFixed(0);
      if (time > 3) {
        let currentData;
        try {
          currentData = JSON.parse(
            localStorage.getItem("zarastanie_video_progress")
          );
        } catch (error) {}
        let obj = {
          ...currentData,
          [pathId]: {
            videoId: currentVideoId,
            time: playerRef.current.currentTime().toFixed(0),
          },
        };
        localStorage.setItem("zarastanie_video_progress", JSON.stringify(obj));
      }
    }

    return () => clearInterval(interval);
  }, [playerRef, currentVideoId, pathId]);

  /**
   * Handle timeline seek
   */
  const onChapterProgressClick = (e) => {
    const didUserChangedChapter =
      parseInt(e.target.id) !== parseInt(currentVideoId);
    if (didUserChangedChapter) {
      return chapterChange.set(e.target.id);
    }
    const x = e.pageX - e.target.getBoundingClientRect().left;
    const clickedValue = (x * e.target.max) / e.target.offsetWidth;
    setVideosProgress((currentvideosProgress) => {
      return {
        ...currentvideosProgress,
        [currentVideoId]: parseInt(clickedValue),
      };
    });

    const time = (playerRef.current.duration() * parseInt(clickedValue)) / 100;
    onVideoTimeUpdateHandler(null, time);
  };

  /**
   * Handle video time update
   */
  const onVideoTimeUpdateHandler = (e, forcedTime) => {
    if (!playerRef.current) {
      return;
    }
    let duration, currentTime;
    try {
      duration = playerRef.current?.duration();
      currentTime = playerRef?.current?.currentTime();
    } catch (error) {
      return;
    }
    if (forcedTime && !playerRef?.current?.paused()) {
      playerRef.current.currentTime(forcedTime);
    }

    if (forcedTime && playerRef?.current?.paused()) {
      playerRef.current.play();
      playerRef.current.currentTime(forcedTime);
    }
    if (!duration || !currentTime) {
      return;
    }
    const getProcentage = () => {
      return (forcedTime ? forcedTime : currentTime / duration) * 100;
    };
    const procentage = getProcentage();
    if (!forcedTime) {
      setVideosProgress((currentvideosProgress) => {
        return {
          ...currentvideosProgress,
          [currentVideoId]: parseInt(procentage),
        };
      });
    }
    const time = durationToTime(currentTime);

    setVideoInfo((currentVideoInfo) => {
      return {
        ...currentVideoInfo,
        currentTime: time,
      };
    });
  };

  /**
   * Autoplay next chapter
   */
  const onVideoEndedHandler = (e) => {
    chapterChange.next();
  };

  /**
   * Toggle play on video click
   */
  const onVideoClickHandler = () => {
    if (playerRef?.current?.paused()) {
      playerRef.current.play();
    } else {
      playerRef.current.pause();
    }
  };

  const onVideoPlayHandler = () => {
    toggleVideoPlay(true);
  };

  const onVideoPauseHandler = () => {
    toggleVideoPlay(false);
  };

  const onVideoError = (e) => {
    console.warn({ e });
  };

  /**
   * Update videoInfo after load meta video
   */
  const onVideoLoadedMetadataHandler = () => {
    const time = durationToTime(playerRef.current.duration());
    const currentTime = durationToTime(playerRef.current.currentTime());

    setVideoInfo((currentVideoInfo) => {
      return {
        ...currentVideoInfo,
        currentTime: currentTime,
        duration: time,
      };
    });
  };

  const onPlayerReadyHandler = (player) => {
    playerRef.current = player;

    if (i18next.resolvedLanguage !== "en") return;
    if (playerRef?.current?.remoteTextTracks().length > 0) return;

    initialVideosData.forEach((videoSource) => {
      if (videoSource.id === currentVideoId) {
        playerRef?.current?.addRemoteTextTrack(
          {
            kind: "captions",
            srclang: "en",
            src: videoSource.textTracks,
            mode: "showing",
            default: true,
            label: "en",
          },
          true
        );
      }
    });
  };

  const chapterChange = {
    set: (nextVideoId) => {
      setCurrentVideoId(parseInt(nextVideoId));
      playerRef?.current?.src(videosSources[parseInt(nextVideoId)]);
      const nextVideoIndex = initialVideosData.findIndex(
        (video) => video.id === parseInt(nextVideoId)
      );
      setVideosProgress((currentvideosProgress) => {
        let newVideosProgress = {};
        Object.keys(currentvideosProgress).forEach((videoId, index) => {
          newVideosProgress = {
            ...newVideosProgress,
            [videoId]: index >= nextVideoIndex ? 0 : 100,
          };
        });
        return newVideosProgress;
      });
    },
    next: () => {
      const index = initialVideosData.findIndex((video) => {
        return video.id === currentVideoId;
      });
      const nextVideoIndex = index + 1;
      if (nextVideoIndex >= initialVideosData.length) {
        chapterChange.set(0);

        let currentData;
        try {
          currentData = JSON.parse(
            localStorage.getItem("zarastanie_video_progress")
          );
        } catch (error) {}
        let obj = {
          ...currentData,
          [pathId]: {},
        };
        localStorage.setItem("zarastanie_video_progress", JSON.stringify(obj));

        navigate("/scene");

        return console.warn("Unable to watch next video");
      }
      setVideosProgress((currentvideosProgress) => {
        return {
          ...currentvideosProgress,
          [currentVideoId]: 100,
        };
      });
      const nextVideoId = initialVideosData[nextVideoIndex]?.id;
      setCurrentVideoId(parseInt(nextVideoId));
      playerRef.current.src(videosSources[parseInt(nextVideoId)]);
    },
    prev: () => {
      const index = initialVideosData.findIndex((video) => {
        return video.id === currentVideoId;
      });
      const nextVideoIndex = index - 1;
      if (nextVideoIndex < 1) {
        return console.error("Unable to watch previous video");
      }
      setVideosProgress((currentvideosProgress) => {
        return {
          ...currentvideosProgress,
          [currentVideoId]: 0,
        };
      });
      const nextVideoId = initialVideosData[nextVideoIndex].id;
      setCurrentVideoId(parseInt(nextVideoId));
      playerRef.current.src(videosSources[parseInt(nextVideoId)]);
    },
    startAt: (videoId, startTime) => {
      chapterChange.set(videoId);
      onVideoTimeUpdateHandler(null, startTime);
    },
  };

  const progress =
    videosProgress &&
    Object.entries(videosProgress).map((videoProgress, index) => {
      return (
        <Chapter
          key={videoProgress[0]}
          id={videoProgress[0]}
          data={[
            videoProgress,
            index,
            initialVideosData,
            currentVideoId,
            playerRef.current?.duration(),
          ]}
          onChapterProgressClick={onChapterProgressClick}
        />
      );
    });

  return (
    <div className="video__container">
      <VideoJS
        options={videoJsOptions}
        onPlayerReady={onPlayerReadyHandler}
        onVideoTimeUpdate={onVideoTimeUpdateHandler}
        onVideoEnded={onVideoEndedHandler}
        onVideoClick={onVideoClickHandler}
        onVideoPlay={onVideoPlayHandler}
        onVideoPause={onVideoPauseHandler}
        onVideoLoadedMetadata={onVideoLoadedMetadataHandler}
        onVideoError={onVideoError}
        currentVideoI={currentVideoId}
      />
      <VideoPlayerControls
        onVideoClickHandler={onVideoClickHandler}
        isVideoPlaying={isVideoPlaying}
        progress={progress}
        videoInfo={videoInfo}
        pathName={pathName}
      />
    </div>
  );
};

export const durationToTime = (duration) => {
  let mzminutes = Math.floor(duration / 60);
  let mzseconds = Math.floor(duration - mzminutes * 60);
  if (mzseconds < 10) {
    mzseconds = `0${mzseconds}`;
  }
  return mzminutes + ":" + mzseconds;
};
export default VideoPlayer;
